import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { initApiClient } from '@memorai/api-hooks';
import { getWebConfig } from '@memorai/config';
import { LocaleProvider, useTranslations } from '@memorai/i18n';
import { ThemeProvider } from './lib/ThemeProvider';
import { queryClient } from './lib/queryClient';
import { MemoraiErrorBoundary } from './lib/components/memorai';
import { AuthProvider } from './contexts/AuthContext';
import App from './App';
import { supabase } from './lib/supabase';
import { ResolvedLocaleProvider, useResolvedLocale } from './lib/i18n/useResolvedLocale';
import './index.css';

const webConfig = getWebConfig(import.meta.env);
initApiClient({
    apiUrl: webConfig.apiUrl,
    supabase,
});

function AppErrorBoundary({ children }: { children: React.ReactNode }) {
    const { t } = useTranslations('errors');

    return (
        <MemoraiErrorBoundary
            title={t('rootBoundary.title')}
            description={t('rootBoundary.description')}
            reloadLabel={t('rootBoundary.reloadLabel')}
            technicalDetailsLabel={t('rootBoundary.technicalDetailsLabel')}
        >
            {children}
        </MemoraiErrorBoundary>
    );
}

function AppProviders() {
    const { locale } = useResolvedLocale();

    return (
        <LocaleProvider locale={locale}>
            <ThemeProvider>
                <BrowserRouter>
                    <AppErrorBoundary>
                        <App />
                    </AppErrorBoundary>
                </BrowserRouter>
            </ThemeProvider>
        </LocaleProvider>
    );
}

ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <MemoraiErrorBoundary>
            <QueryClientProvider client={queryClient}>
                <AuthProvider>
                    <ResolvedLocaleProvider>
                        <AppProviders />
                    </ResolvedLocaleProvider>
                </AuthProvider>
            </QueryClientProvider>
        </MemoraiErrorBoundary>
    </React.StrictMode>
);
